import { Vector3 } from "three";
import { GeometryUtils, isNull, PrecisionUtils } from "../../infra3d";
import { GEOMETRYTYPE } from "../featuretree/internal";
import { MEASUREMODE } from "../internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { Tool } from "./internal";
/**
 * Draw LineString Tool
 * @class DrawLineStringTool
 * @extends {Tool}
 */
export class DrawLineStringTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        // event variables
        this.aeventnr = null;
        this.deventnr = null;
        this.doubletapeventnr = null;
        this.drageventnr = null;
        this.finishinputevent = null;
        this.holdeventnr = null;
        this.mousemoveeventnr = null;
        this.mouseupeventnr = null;
        this.mouseouteventnr = null;
        this.releaseeventnr = null;
        this.resetinputevent = null;
        this.swipeeventnr = null;
        this.undoinputevent = null;
        // other variables
        this._last_mx = null;
        this._last_my = null;
        this.evt = null;
        this.auxcollection = null;
        this.auxlineActive = false;
        this.auxlinestringfeature = null;
        this.coordinates = [];
        this.distance = 0;
        this.featurecollection = null;
        this.lastpos = null;
        this.linestringfeature = null;
        this.longpressActive = false;
        this.multipointfeature = null;
        this.mx = null;
        this.my = null;
        this.pointfeature = null;
        this.pointpending = false;
        this.pos = null;
        this.posArray = [];
        this.snap_mx = null;
        this.snap_my = null;
        this.snappedpos = null;
        this.tempcoordinates = [];
        this.tempposarray = [];
        this.toolstate = 0;
        this.touch_mx = null;
        this.touch_my = null;
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const defaultoptions = {
            pointsetcbf: null,
            undoinput: USERINPUTTYPE.KEY_DELETE,
            calcdistance: true,
            precGlobalTreshold: 5.0, // NOTE: [sec]
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        //create the drawing layers
        this.featurecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.layerstyle, this.options.highlightlayerstyle, "mmDrawingToolLineString_internal", false);
        // create auxiliary collection
        this.auxcollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.toolhandler.defaultstyles.orthobase, null, "mmDrawingToolLineStringAux_internal");
        // create auxiliary feature
        this.auxlinestringfeature = this.auxcollection.createFeature();
        // create auxiliary line geometry
        const geometry = this.auxlinestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        // add geometry to auxiliary line feature
        this.auxlinestringfeature.geometry = geometry;
        // hide line by default
        this.auxcollection.hide();
        this.pointfeature = this.featurecollection.createFeature();
        this.pointfeature.geometry = this.pointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.multipointfeature = this.featurecollection.createFeature();
        this.multipointfeature.geometry = this.multipointfeature.createGeometry(GEOMETRYTYPE.MULTIPOINT);
        this.linestringfeature = this.featurecollection.createFeature();
        this.linestringfeature.geometry = this.linestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.type = "DrawLineStringTool";
    }
    enableAuxline() {
        this.auxlineActive = true;
        this.auxcollection.show();
    }
    disableAuxline() {
        this.auxlineActive = false;
        this.auxcollection.hide();
    }
    /**
     * internal function - to activate tool call mmSelectFeatureTool.enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        this.enabled = true;
        this._SubscribeEvents();
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        }
        this.fire(DrawLineStringTool.enabled, this.options["cbobject"]);
        this.toolstate = 0;
    }
    /**
     * internal function - to disable tool call mmSelectFeatureTool.disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        this.enabled = false;
        this.reset(null);
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle("default");
            this.scene.getMeasureModeHandler().hideMeasureModeCursor();
        }
        this.fire(DrawLineStringTool.disabled, this.options["cbobject"]);
        this._unSubscribeEvents();
        this.toolstate = -1;
    }
    /**
     * subscribe all needed events
     * @private
     */
    _SubscribeEvents() {
        //check if events aren't already subscribed
        if (!this.enabled || this.mouseupeventnr != null)
            return;
        this.mouseupeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEUP, this._onMouseUp, this);
        this.doubletapeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.DOUBLETAP, this._onDoubletap, this);
        this.aeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_A, this._onA, this);
        this.deventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_D, this._onD, this);
        if (this.options.undoinput) {
            this.undoinputevent = this.scene.userInputHandler.subscribeEvent(this.options.undoinput, this._undo, this);
        }
        if (this.options.resetinput) {
            this.resetinputevent = this.scene.userInputHandler.subscribeEvent(this.options.resetinput, this.reset, this);
        }
        if (this.options.finishinput) {
            this.finishinputevent = this.scene.userInputHandler.subscribeEvent(this.options.finishinput, this.finishmeasurement, this);
        }
        this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        this.mouseouteventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEOUT, this._onMouseOut, this);
        if (this.options.enablecbf != null) {
            this.on(DrawLineStringTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(DrawLineStringTool.disabled, this.options.disablecbf);
        }
        if (this.options.pointsetcbf != null) {
            this.on(DrawLineStringTool.pointset, this.options.pointsetcbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.on(DrawLineStringTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.resetcbf != null) {
            this.on(DrawLineStringTool.reset, this.options.resetcbf);
        }
        if (this.options.finishedcbf != null) {
            this.on(DrawLineStringTool.finished, this.options.finishedcbf);
        }
        if (this.options.errorcbf != null) {
            this.on(DrawLineStringTool.error, this.options.errorcbf);
        }
    }
    _unSubscribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEUP, this.mouseupeventnr);
        this.mouseupeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEOUT, this.mouseouteventnr);
        this.mouseouteventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.DOUBLETAP, this.doubletapeventnr);
        this.doubletapeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_A, this.aeventnr);
        this.aeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_D, this.deventnr);
        this.deventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.undoinput, this.undoinputevent);
        this.undoinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.resetinput, this.resetinputevent);
        this.resetinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.finishinput, this.finishinputevent);
        this.finishinputevent = null;
        this.scene.getMeasureModeHandler().hideMeasureModeCursor();
        if (this.options.enablecbf != null) {
            this.off(DrawLineStringTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(DrawLineStringTool.disabled, this.options.disablecbf);
        }
        if (this.options.pointsetcbf != null) {
            this.off(DrawLineStringTool.pointset, this.options.pointsetcbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.off(DrawLineStringTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.resetcbf != null) {
            this.off(DrawLineStringTool.reset, this.options.resetcbf);
        }
        if (this.options.finishedcbf != null) {
            this.off(DrawLineStringTool.finished, this.options.finishedcbf);
        }
        if (this.options.errorcbf != null) {
            this.off(DrawLineStringTool.error, this.options.errorcbf);
        }
    }
    /**
     * The doubletap event.
     * @method _onDoubletap
     * @param evt
     * @private
     */
    _onDoubletap(evt) {
        this.reset(evt);
    }
    /**
     * internal function mousemove
     * @method _onMouseMove
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        if (this.pointfeature.ishidden) {
            this.pointfeature.show();
        }
        this._measureOnMouseMove(evt);
    }
    /**
     * internal function mouseout
     * @method _onMouseOut
     * @param evt
     * @private
     */
    _onMouseOut(evt) {
        this.pointfeature.hide();
        this.pointfeature.update(true);
    }
    /**
     * Mouse up event
     * @method _onMouseUp
     * @param evt
     * @private
     */
    _onMouseUp(evt) {
        if (evt.button > 0) {
            return;
        }
        if (evt.isDragging) {
            this._measureOnMouseMove(evt);
        }
        else {
            const mx = evt.offsetX;
            const my = evt.offsetY;
            this.evt = evt;
            const measuremode = this.scene.getMeasureMode();
            this._unSubscribeEvents();
            //the reset event needs to be still active
            if (this.options.resetinput != null) {
                this.resetinputevent = this.scene.userInputHandler.subscribeEvent(this.options.resetinput, this.reset, this);
            }
            if (this.snappedpos == null) {
                this.scene.getMeasureHandler().createMeasurePointInMeasureMode(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist, (pos) => {
                    if (!pos.warning)
                        this._posRequestSuccess(pos);
                    else {
                        setTimeout(() => {
                            this._SubscribeEvents();
                        }, 10);
                    }
                }, (pos) => {
                    this._posRequestFailed(pos);
                }, measuremode);
            }
            else {
                this._posRequestSuccess(this.snappedpos.copy());
            }
        }
    }
    /**
     * finds and sets starting point for this meassure on the selected feature
     * @private
     */
    _setSelectedStart() {
        if (!isNull(this.selectedFeature) && this.coordinates.length <= 1) {
            super.findOrthogonal(this.pos);
            if (!isNull(this.selectedStart)) {
                this.coordinates[0] = this.selectedStart;
                this._draw(this.coordinates);
                this.tempcoordinates[0] = this.selectedStart;
                this.tempcoordinates[1] = this.pos;
                this._draw(this.tempcoordinates);
                const distance = this._calcDistance(this.tempcoordinates);
                const distance2d = this._calcDistance2D(this.tempcoordinates);
                const height = this._calcHeight(this.tempcoordinates);
                const precDistance = this._calcPrecisionDistance(this.tempcoordinates);
                const precDistance2d = this._calcPrecisionDistance2D(this.tempcoordinates);
                const precHeight = this._calcPrecisionHeight(this.tempcoordinates);
                const mh = this.scene.getMeasureHandler();
                const mapinfo = mh.worldToMapinfo(this.selectedStart.getVec3());
                if (mapinfo.length > 0) {
                    const mxmy2 = mh.mapToMouse(mapinfo[0].coords.x, mapinfo[0].coords.y);
                    this.fire(DrawLineStringTool.pointset, this.options["cbobject"], {
                        mx: mxmy2.x,
                        my: mxmy2.y,
                        distance: distance,
                        distance2d: distance2d,
                        height: height,
                        precDistance: precDistance.precision,
                        precDistance2d: precDistance2d.precision,
                        precHeight: precHeight.precision,
                        precMode: precDistance.mode,
                        pos: this.selectedStart,
                        linestringfeature: this.linestringfeature.toGeoJSON(),
                    }, this.evt);
                }
                this.fire(DrawLineStringTool.continousdraw, this.options["cbobject"], {
                    mx: this.mx,
                    my: this.my,
                    distance: distance,
                    distance2d: distance2d,
                    height: height,
                    precDistance: precDistance.precision,
                    precDistance2d: precDistance2d.precision,
                    precHeight: precHeight.precision,
                    precMode: precDistance.mode,
                    pos: this.pos,
                    linestringfeature: this.linestringfeature.toGeoJSON(),
                }, this.evt);
            }
        }
    }
    /**
     * offset for featureline segment if start is orthogonally +1
     * and redraw
     * @method _onOne
     * @private
     */
    _onA() {
        console.log("Deprecated ?? drawlinestringtool.js onA");
        this.featureoffset++;
        this._setSelectedStart();
    }
    /**
     * offset for featureline segment if start is orthogonally -1
     * and redraw
     * @method _onOne
     * @private
     */
    _onD() {
        console.log("Deprecated ?? drawlinestringtool.js onD");
        this.featureoffset--;
        this._setSelectedStart();
    }
    /**
     * calculates the world coordinate and displays it as point in scene
     * @method _measureOnMouseMove
     * @param evt
     * @private
     */
    _measureOnMouseMove(evt) {
        const mx = evt.offsetX;
        const my = evt.offsetY;
        this.evt = evt;
        //to prevent repeated points error in poly2tri...
        if (this._last_mx == mx || this._last_my == my)
            return;
        if (this.scene.getMeasureModeHandler().overrideMeasureMode != null) {
            //measure mode is overwritten so set last point to second last point
            const len = this.tempcoordinates.length;
            if (len > 1) {
                this.tempcoordinates[len - 1] = this.tempcoordinates[len - 2];
            }
            this._draw(this.tempcoordinates);
            if (this.options.modifycursor) {
                this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
                this.scene.getMeasureModeHandler().hideMeasureModeCursor();
            }
            return;
        }
        const mousemovemeasuremode = this.scene.getMeasureModeHandler().mouseMoveMeasureMode;
        if (mousemovemeasuremode == MEASUREMODE.NONE) {
            if (this.options.modifycursor) {
                this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
                this.scene.getMeasureModeHandler().hideMeasureModeCursor();
            }
            return;
        }
        this._checkSnaping(evt);
        if (this.snappedpos) {
            this._pointfound(this.snappedpos);
            return;
        }
        if (this.pointpending)
            return;
        this.pointpending = true;
        //make a measurepoint in mousemove measuremode
        this.scene.getMeasureHandler().createMeasurePointInMeasureMode(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist, (pos) => {
            this._pointfound(pos);
        }, (msg) => {
            this._pointNotFound(msg);
        }, this.scene.getMeasureModeHandler().mouseMoveMeasureMode);
    }
    _pointNotFound(msg) {
        this.pointpending = false;
        if (!this.enabled)
            return;
        if (this.scene.getMeasureMode() == MEASUREMODE.PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED ||
            this.scene.getMeasureMode() == MEASUREMODE.PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED) {
            this.scene.getMeasureModeHandler().showMeasureModeCursor();
        }
        else if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle("not-allowed");
        }
        if (this.toolstate > 0) {
            this.fire(DrawLineStringTool.error, this.options["cbobject"], msg, this.evt);
        }
        this.pointfeature.hide();
        return;
    }
    _pointfound(pos) {
        this.pointpending = false;
        if (!this.enabled)
            return;
        this.pos = pos;
        this.pointfeature.show();
        this._setSelectedStart();
        if (this.tempcoordinates.length > 0) {
            this.tempcoordinates[this.tempcoordinates.length - 1] = this.pos;
        }
        else {
            this.tempcoordinates[0] = this.pos;
        }
        // store position for  undo event...
        this.lastpos = this.pos;
        if (this.options.modifycursor) {
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
            this.scene.getMeasureModeHandler().hideMeasureModeCursor();
        }
        if (this.tempcoordinates.length > 0) {
            this._draw(this.tempcoordinates);
            const distance = this._calcDistance(this.tempcoordinates);
            const distance2d = this._calcDistance2D(this.tempcoordinates);
            const height = this._calcHeight(this.tempcoordinates);
            const precDistance = this._calcPrecisionDistance(this.tempcoordinates);
            const precDistance2d = this._calcPrecisionDistance2D(this.tempcoordinates);
            const precHeight = this._calcPrecisionHeight(this.tempcoordinates);
            if (this.toolstate > 0) {
                this.fire(DrawLineStringTool.continousdraw, this.options["cbobject"], {
                    mx: this.evt.offsetX,
                    my: this.evt.offsetY,
                    distance: distance,
                    distance2d: distance2d,
                    height: height,
                    precDistance: precDistance.precision,
                    precDistance2d: precDistance2d.precision,
                    precHeight: precHeight.precision,
                    precMode: precDistance.mode,
                    pos: this.pos,
                    linestringfeature: this.linestringfeature.toGeoJSON(),
                }, this.evt);
            }
        }
    }
    /**
     * if snaped layers are activ position will be snaped to their features basepoints
     * otherwise position stays the same
     * @param evt
     * @returns {any}
     * @private
     */
    _checkSnaping(evt) {
        const mx = evt.offsetX;
        const my = evt.offsetY;
        if (this.options.snaplayer && !evt.altKey) {
            const snapdataarray = this.scene.getFeatureHandler().snap(mx, my, this.options.snaplayer);
            //prevent self pickcing and filter non-pointy geometries
            for (let i = snapdataarray.length - 1; i >= 0; i--) {
                if (snapdataarray[i].geometry.type != GEOMETRYTYPE.MULTIPOINT &&
                    snapdataarray[i].geometry.type != GEOMETRYTYPE.POINT) {
                    snapdataarray.splice(i, 1);
                }
            }
            if (snapdataarray.length > 0) {
                const snapdata = snapdataarray[0];
                this.snap_mx = snapdata.found_mx;
                this.snap_my = snapdata.found_my;
                this.pos.Easting = snapdata.found_easting;
                this.pos.Northing = snapdata.found_northing;
                this.pos.Height = snapdata.found_height;
                this.pos.warning = false;
                this.snappedpos = this.pos;
            }
            else {
                this.snap_mx = null;
                this.snap_my = null;
                this.snappedpos = null;
            }
        }
    }
    /**
     * no valid position was found; cleanup
     * @param pos
     * @private
     */
    _posRequestFailed(pos) {
        this.reset();
        this.scene.getMeasureHandler().abortMeasureSequence();
        setTimeout(() => {
            this._SubscribeEvents();
        }, 10);
    }
    /**
     * valid position found; save it
     * @param pos
     * @private
     */
    _posRequestSuccess(pos) {
        setTimeout(() => {
            this._SubscribeEvents();
        }, 10);
        //--- draw a polgon State Machine
        switch (this.toolstate) {
            case 0: //first click, create an new linestring, unless first point was already set by selected feature
                this.toolstate += 1;
                if (!this.selectedFeature) {
                    if (this.coordinates.length > 0)
                        this._cleanUp();
                    if (pos.isValid())
                        this.coordinates[0] = pos;
                    break;
                }
            // falls through
            case 1: //second click append coordinates
                if (pos.isValid()) {
                    const mmode = this.scene.getMeasureMode();
                    if (mmode == MEASUREMODE.LEFTRIGHT ||
                        mmode == MEASUREMODE.MULTIVIEW ||
                        mmode == MEASUREMODE.PANODEPTHMAPINTERSECTION ||
                        mmode == MEASUREMODE.PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED ||
                        mmode == MEASUREMODE.PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED ||
                        mmode == MEASUREMODE.PANO_TWO_CLICK) {
                        this.pos = pos;
                        this._setSelectedStart();
                        if (this.orthoBase)
                            this._recalcFirstPoint(pos);
                    }
                    if (this.orthoBase && this.pos) {
                        this.coordinates.push(this.pos);
                    }
                    else {
                        this.coordinates.push(pos);
                    }
                }
                break;
        }
        this._draw(this.coordinates);
        const distance = this._calcDistance(this.coordinates);
        const distance2d = this._calcDistance2D(this.coordinates);
        const height = this._calcHeight(this.coordinates);
        const precDistance = this._calcPrecisionDistance(this.coordinates);
        const precDistance2d = this._calcPrecisionDistance2D(this.coordinates);
        const precHeight = this._calcPrecisionHeight(this.coordinates);
        this.fire(DrawLineStringTool.pointset, this.options["cbobject"], {
            mx: pos.mx,
            my: pos.my,
            distance: distance,
            distance2d: distance2d,
            height: height,
            precDistance: precDistance.precision,
            precDistance2d: precDistance2d.precision,
            precHeight: precHeight.precision,
            precMode: precDistance.mode,
            pos: pos,
            linestringfeature: this.linestringfeature.toGeoJSON(),
        }, this.evt);
        this.tempcoordinates = this.coordinates.slice(0);
        this.tempcoordinates.push(pos);
        this._last_mx = this.evt.offsetX;
        this._last_my = this.evt.offsetY;
    }
    /**
     *  Draws the feature into the scene
     * @method _draw
     * @param {MeasurePoint[]} coors the geometry coordinates
     * @private
     */
    _draw(coors) {
        const coorsarray = [];
        for (let i = 0; i < coors.length; i++) {
            coorsarray.push([coors[i].Easting, coors[i].Northing, coors[i].Height]);
        }
        //Update the features for drawing
        if (coorsarray.length > 0) {
            this.pointfeature.geometry.coordinates = [coorsarray[coorsarray.length - 1]];
        }
        if (coorsarray.length > 1) {
            this.multipointfeature.geometry.coordinates = coorsarray;
            this.linestringfeature.geometry.coordinates = coorsarray.slice(0);
            // draw auxiliary line
            const coords = coorsarray;
            const auxcoords = [];
            if (coords.length > 0) {
                const z = coords[0][2];
                for (let i = 0; i < coords.length; i++) {
                    auxcoords.push([coords[i][0], coords[i][1], z]);
                }
                auxcoords.push(coords[coords.length - 1]);
            }
            this.auxlinestringfeature.geometry.coordinates = auxcoords;
            this.auxcollection.update();
        }
        this.featurecollection.update();
    }
    /**
     * Needed if second point was made with LeftRight Meassure -> First Point has to be replaced to keep orthogonality
     * @param pos
     * @private
     */
    _recalcFirstPoint(pos) {
        const p1 = new Vector3(this.orthoBase.geometry.node.coordinates[0][0], this.orthoBase.geometry.node.coordinates[0][1], this.orthoBase.geometry.node.coordinates[0][2]);
        const p2 = new Vector3(this.orthoBase.geometry.node.coordinates[1][0], this.orthoBase.geometry.node.coordinates[1][1], this.orthoBase.geometry.node.coordinates[1][2]);
        const r_line = new Vector3(p1.x, p1.y, p1.z);
        let a_line = p2.sub(p1);
        let p = pos.getVec3();
        p = p.sub(r_line);
        const a = new Vector3(a_line.x, a_line.y, a_line.z);
        const b = new Vector3(p.x, p.y, p.z);
        const na = a_line.length();
        a_line = a_line.cross(p);
        const nb = a_line.length();
        const d = nb / na;
        this.distance = d;
        const f = new Vector3(a.x, a.y, a.z).dot(b) / a.lengthSq();
        const val = [a.x * f + r_line.x, a.y * f + r_line.y, a.z * f + r_line.z];
        this.coordinates[0].Easting = val[0];
        this.coordinates[0].Northing = val[1];
        this.coordinates[0].Height = val[2];
    }
    /**
     * internal function - Finish measurement
     * @method finishmeasurement
     */
    finishmeasurement(evt) {
        this._draw(this.coordinates);
        if (this.toolstate == 0) {
            this.reset();
            return;
        }
        super.removeOrthoBase();
        this.toolstate = 0;
        this.selectedFeature = null;
        this.selectedStart = null;
        this.tempcoordinates = [];
        const linestringfeature = this.linestringfeature.toGeoJSON();
        /* check if the last two points are not equal -> occurs if finished by double-click...*/
        const numpoints = linestringfeature.geometry.coordinates.length;
        if (numpoints >= 2) {
            if (linestringfeature.geometry.coordinates[numpoints - 1][0] ==
                linestringfeature.geometry.coordinates[numpoints - 2][0] &&
                linestringfeature.geometry.coordinates[numpoints - 1][1] ==
                    linestringfeature.geometry.coordinates[numpoints - 2][1] &&
                linestringfeature.geometry.coordinates[numpoints - 1][2] ==
                    linestringfeature.geometry.coordinates[numpoints - 2][2]) {
                linestringfeature.geometry.coordinates.splice(numpoints - 1, 1);
                this.coordinates.splice(numpoints - 1, 1);
                this.tempcoordinates.splice(numpoints - 1, 1);
            }
            const distance = this._calcDistance(this.coordinates);
            const distance2d = this._calcDistance2D(this.coordinates);
            const height = this._calcHeight(this.coordinates);
            const precDistance = this._calcPrecisionDistance(this.coordinates);
            const precDistance2d = this._calcPrecisionDistance2D(this.coordinates);
            const precHeight = this._calcPrecisionHeight(this.coordinates);
            const pos = this.coordinates.slice(-1)[0];
            this.fire(DrawLineStringTool.finished, this.options["cbobject"], {
                linestringfeature: linestringfeature,
                mx: pos["mouseX"],
                my: pos["mouseY"],
                measurepoint: pos,
                distance: distance,
                distance2d: distance2d,
                height: height,
                precDistance: precDistance.precision,
                precDistance2d: precDistance2d.precision,
                precHeight: precHeight.precision,
                precMode: precDistance.mode,
            }, evt);
        }
    }
    _undo(evt) {
        if (this.coordinates.length > 0) {
            this.coordinates.pop();
            this.tempcoordinates.pop();
            this.tempcoordinates[this.tempcoordinates.length - 1] = this.lastpos;
            this._draw(this.tempcoordinates);
            const distance = this._calcDistance(this.tempcoordinates);
            const distance2d = this._calcDistance2D(this.tempcoordinates);
            const height = this._calcHeight(this.tempcoordinates);
            const precDistance = this._calcPrecisionDistance(this.tempcoordinates);
            const precDistance2d = this._calcPrecisionDistance2D(this.tempcoordinates);
            const precHeight = this._calcPrecisionHeight(this.tempcoordinates);
            this.fire(DrawLineStringTool.continousdraw, this.options["cbobject"], {
                mx: this.mx,
                my: this.my,
                distance: distance,
                distance2d: distance2d,
                height: height,
                precDistance: precDistance.precision,
                precDistance2d: precDistance2d.precision,
                precHeight: precHeight.precision,
                precMode: precDistance.mode,
                pos: this.lastpos,
                linestringfeature: this.linestringfeature.toGeoJSON(),
            }, evt);
        }
    }
    /**
     * removes all drawing from the scene
     * @method _cleanUp
     * @private
     */
    _cleanUp() {
        this.scene.getMeasureHandler().abortMeasureSequence();
        this.pointpending = false;
        if (this.coordinates.length > 0) {
            this.coordinates = [];
            this.tempcoordinates = [];
            this.tempposarray = [];
            this.posArray = [];
        }
        this.multipointfeature.geometry.clear();
        this.linestringfeature.geometry.clear();
        this.pointfeature.geometry.clear();
        this.auxlinestringfeature.geometry.clear();
        this.selectedFeature = null;
        this.selectedStart = null;
        this.featurecollection.update();
    }
    /**
     *  resets the tool and calls the aborted event
     *  @method reset
     *  @param {?Object} evt EventObject if called from userinput
     *  @pubilc
     */
    reset(evt) {
        super.removeOrthoBase();
        this._cleanUp();
        this.toolstate = 0;
        this.scene.infrascene.render();
        this.fire(DrawLineStringTool.reset, this.options["cbobject"], evt);
    }
    destroy() {
        this._cleanUp();
        this.featurecollection.destroy();
        this.featurecollection = null;
        this.auxcollection.destroy();
        this.auxcollection = null;
    }
    /**
     * internal function - to calculate the distance from a coordinate-Array
     * @method _calcDistance
     * @param {MeasurePoint[]} coords
     * @return {number}
     * @private
     */
    _calcDistance(coords) {
        if (!this.options.calcdistance)
            return 0.0;
        return GeometryUtils.CalcDistanceLine(coords);
    }
    _calcDistance2D(coords) {
        if (!this.options.calcdistance)
            return 0.0;
        return GeometryUtils.CalcDistanceLine2D(coords);
    }
    _calcHeight(coords) {
        if (!this.options.calcdistance)
            return 0.0;
        return GeometryUtils.CalcHeightLine(coords);
    }
    _calcPrecisionDistance(coords) {
        if (!this.options.calcdistance) {
            return {
                precision: 0.0,
                mode: null,
            };
        }
        const mode = this._precisionMode(coords);
        if (mode === "local") {
            return {
                precision: PrecisionUtils.lineLocal(coords),
                mode: mode,
            };
        }
        else if (mode === "global") {
            return {
                precision: PrecisionUtils.lineGlobal(coords),
                mode: mode,
            };
        }
    }
    _calcPrecisionDistance2D(coords) {
        if (!this.options.calcdistance) {
            return {
                precision: 0.0,
                mode: null,
            };
        }
        const mode = this._precisionMode(coords);
        if (mode === "local") {
            return {
                precision: PrecisionUtils.lineLocal2D(coords),
                mode: mode,
            };
        }
        else if (mode === "global") {
            return {
                precision: PrecisionUtils.lineGlobal2D(coords),
                mode: mode,
            };
        }
    }
    _calcPrecisionHeight(coords) {
        if (!this.options.calcdistance) {
            return {
                precision: 0.0,
                mode: null,
            };
        }
        const mode = this._precisionMode(coords);
        if (mode === "local") {
            return {
                precision: PrecisionUtils.heightLineLocal(coords),
                mode: mode,
            };
        }
        else if (mode === "global") {
            return {
                precision: PrecisionUtils.heightLineGlobal(coords),
                mode: mode,
            };
        }
    }
    _precisionMode(coords) {
        const timestamps = [];
        for (let i = 0; i < coords.length; i++) {
            timestamps.push(new Date(coords[i].geoframe.timestamp) / 1000.0);
        }
        const diff = Math.max(...timestamps) - Math.min(...timestamps);
        if (diff > this.options.precGlobalTreshold) {
            return "global";
        }
        return "local";
    }
}
DrawLineStringTool.enabled = "enabled";
DrawLineStringTool.disabled = "disabled";
DrawLineStringTool.pointset = "pointset";
DrawLineStringTool.continousdraw = "continousdraw";
DrawLineStringTool.reset = "reset";
DrawLineStringTool.finished = "finished";
DrawLineStringTool.error = "error";
//# sourceMappingURL=drawlinestringstool.js.map